import { emitter } from 'shared/helpers/mitt';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { reconcilePatientTabColors } from 'dashboard/helper/patientTabColors';

export const MAX_TABS = 15;

const CONTEXT_FIELDS = [
  'inboxId',
  'label',
  'teamId',
  'conversationType',
  'foldersId',
];

const hasContextValues = context =>
  CONTEXT_FIELDS.some(field => Boolean(context?.[field]));

const isSameContext = (storedContext, incomingContext) => {
  if (!hasContextValues(storedContext) && !hasContextValues(incomingContext)) {
    return true;
  }

  return CONTEXT_FIELDS.every(
    field =>
      (storedContext?.[field] ?? null) === (incomingContext?.[field] ?? null)
  );
};

// Correctness note: assumes the /app/accounts/<id> segment only ever changes
// via a full page reload
export function accountIdFromRoute() {
  const match = window.location.pathname.match(/\/app\/accounts\/(\d+)/);
  return match ? match[1] : '';
}

export function keyForAccount() {
  const id = accountIdFromRoute();
  return id
    ? `${LOCAL_STORAGE_KEYS.CONVERSATION_TABS}::${id}`
    : LOCAL_STORAGE_KEYS.CONVERSATION_TABS;
}

const savedState = LocalStorage.get(keyForAccount());

export const state = {
  activeTabId: savedState?.activeTabId || null,
  tabs: savedState?.tabs || [],
  closedTabs: savedState?.closedTabs || [],
  // Patient key -> PATIENT_TAB_COLORS index.
  tabColors: {},
};

// Also on hydration: a blob saved before this feature has no map.
state.tabColors = reconcilePatientTabColors({
  tabs: state.tabs,
  closedTabs: state.closedTabs,
  colors: savedState?.tabColors,
});

function saveState($state) {
  // Every mutation lands here, so colours reconcile in one place.
  $state.tabColors = reconcilePatientTabColors({
    tabs: $state.tabs,
    closedTabs: $state.closedTabs,
    colors: $state.tabColors,
  });

  LocalStorage.set(keyForAccount(), {
    activeTabId: $state.activeTabId,
    tabs: $state.tabs,
    closedTabs: $state.closedTabs,
    tabColors: $state.tabColors,
  });
}

export const getters = {
  getTabs: $state => $state.tabs,
  getActiveTabId: $state => $state.activeTabId,
  getActiveTab: $state =>
    $state.tabs.find(t => t.id === $state.activeTabId) || null,
  getClosedTabs: $state => $state.closedTabs,
  getTabColors: $state => $state.tabColors,
};

export const actions = {
  addTab({ commit, state: $state }, conversation) {
    if (!conversation || !conversation.id) return false;

    // Check if already exists
    const exists = $state.tabs.find(t => t.id === conversation.id);
    if (exists) {
      if (!isSameContext(exists.context, conversation.context)) {
        commit('UPDATE_TAB_CONTEXT', {
          tabId: conversation.id,
          context: conversation.context,
        });
      }

      // Always update meta to ensure we have the latest sender info (e.g. if contact was anonymized)
      if (conversation.meta) {
        commit('UPDATE_TAB_META', {
          tabId: conversation.id,
          meta: conversation.meta,
        });
      }

      commit('SET_ACTIVE_TAB', conversation.id);
      return true;
    }

    if ($state.tabs.length >= MAX_TABS) {
      emitter.emit('newToastMessage', {
        message: `Tab limit reached (${MAX_TABS}). Please close one to continue.`,
      });
      return false;
    }

    commit('ADD_TAB', conversation);
    commit('SET_ACTIVE_TAB', conversation.id);
    return true;
  },
  removeTab({ commit, state: $state }, tabId) {
    const tabIndex = $state.tabs.findIndex(t => t.id === tabId);
    if (tabIndex === -1) return;

    const tabToRemove = $state.tabs[tabIndex];
    const isRemovingActive = $state.activeTabId === tabId;
    let nextActiveId = null;

    if (isRemovingActive && $state.tabs.length > 1) {
      if (tabIndex < $state.tabs.length - 1) {
        nextActiveId = $state.tabs[tabIndex + 1].id;
      } else {
        nextActiveId = $state.tabs[tabIndex - 1].id;
      }
    }

    commit('REMOVE_TAB', tabId);
    commit('ADD_CLOSED_TAB', tabToRemove);

    if (isRemovingActive && nextActiveId) {
      commit('SET_ACTIVE_TAB', nextActiveId);
    } else if (isRemovingActive && $state.tabs.length === 0) {
      commit('SET_ACTIVE_TAB', null);
    }
  },
  moveTabToFirst({ commit, state: $state }, tabId) {
    const tabIndex = $state.tabs.findIndex(t => t.id === tabId);
    if (tabIndex === -1) return;

    const tab = $state.tabs[tabIndex];
    commit('REMOVE_TAB', tabId);
    commit('ADD_TAB', tab);
  },
  reorderTabs({ commit }, { from, to }) {
    commit('REORDER_TABS', { from, to });
  },
  restoreTab({ commit }, tab) {
    commit('ADD_TAB', tab);
    commit('SET_ACTIVE_TAB', tab.id);
    commit('REMOVE_CLOSED_TAB', tab.id);
  },
  clearTabs({ commit }) {
    commit('CLEAR_TABS');
  },
};

export const mutations = {
  SET_ACTIVE_TAB($state, tabId) {
    $state.activeTabId = tabId;
    saveState($state);
  },
  ADD_TAB($state, conversation) {
    // Add to beginning (left)
    $state.tabs.unshift(conversation);
    // Remove from closed tabs if it exists (reopened)
    $state.closedTabs = $state.closedTabs.filter(t => t.id !== conversation.id);
    saveState($state);
  },
  UPDATE_TAB_CONTEXT($state, { tabId, context }) {
    const tab = $state.tabs.find(item => item.id === tabId);
    if (!tab) return;

    tab.context = context;
    saveState($state);
  },
  UPDATE_TAB_META($state, { tabId, meta }) {
    const tab = $state.tabs.find(item => item.id === tabId);
    if (!tab) return;

    tab.meta = meta;
    saveState($state);
  },
  UPDATE_TAB_CONTACT($state, contact) {
    let updated = false;
    $state.tabs.forEach(tab => {
      if (tab.meta && tab.meta.sender && tab.meta.sender.id === contact.id) {
        tab.meta.sender = { ...tab.meta.sender, ...contact };
        updated = true;
      }
    });
    // Also update closedTabs so if restored, they have the correct sender info
    $state.closedTabs.forEach(tab => {
      if (tab.meta && tab.meta.sender && tab.meta.sender.id === contact.id) {
        tab.meta.sender = { ...tab.meta.sender, ...contact };
        updated = true;
      }
    });
    if (updated) saveState($state);
  },
  REMOVE_TAB($state, tabId) {
    $state.tabs = $state.tabs.filter(t => t.id !== tabId);
    saveState($state);
  },
  REORDER_TABS($state, { from, to }) {
    const last = $state.tabs.length - 1;
    if (!Number.isInteger(from) || !Number.isInteger(to)) return;
    if (from < 0 || from > last || to < 0 || from === to) return;

    // `to` may be one past the end; splice clamps. Always write - the caller's
    // cached slices stay stale otherwise.
    $state.tabs.splice(to, 0, $state.tabs.splice(from, 1)[0]);
    saveState($state);
  },
  ADD_CLOSED_TAB($state, conversation) {
    // Remove existing if present to prevent duplicates and move to top
    $state.closedTabs = $state.closedTabs.filter(t => t.id !== conversation.id);

    $state.closedTabs.unshift(conversation);
    if ($state.closedTabs.length > 10) {
      $state.closedTabs.pop();
    }
    saveState($state);
  },
  REMOVE_CLOSED_TAB($state, tabId) {
    $state.closedTabs = $state.closedTabs.filter(t => t.id !== tabId);
    saveState($state);
  },
  CLEAR_TABS($state) {
    $state.tabs = [];
    $state.activeTabId = null;
    saveState($state);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
